/**
 * File Upload Utilities
 * Pure functions for processing files - NO HOOKS, NO STATE
 */

import { compressImage } from './imageCompression';

// File size limits (in KB)
// NOTE: FT-345 removed the legacy `IMAGE: 50` cap. Images are now handled by
// the FT-340 photo pipeline (`<PhotoAttachment>` → IndexedDB Blob storage).
// The old 50 KB cap was silently rejecting photos between 50 KB and 2 MB when
// the caller passed `maxSizeMB: 2` — the mismatch has been eliminated by
// routing images out of `processFile` entirely (see `useFileUpload.js`).
export const FILE_SIZE_LIMITS = {
  PDF: 2048,        // 2MB for PDFs
  DOCUMENT: 1024,   // 1MB for other documents
  DEFAULT: 500      // 500KB default
};

// Allowed file types
export const ALLOWED_TYPES = {
  IMAGES: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
  PDF: ['application/pdf'],
  DOCUMENTS: ['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  ALL: [] // Will be populated below
};
ALLOWED_TYPES.ALL = [...ALLOWED_TYPES.IMAGES, ...ALLOWED_TYPES.PDF, ...ALLOWED_TYPES.DOCUMENTS];

/**
 * Get file category from MIME type
 */
export function getFileCategory(mimeType) {
  if (ALLOWED_TYPES.IMAGES.includes(mimeType)) return 'image';
  if (ALLOWED_TYPES.PDF.includes(mimeType)) return 'pdf';
  if (ALLOWED_TYPES.DOCUMENTS.includes(mimeType)) return 'document';
  return 'unknown';
}

/**
 * Validate file type
 */
export function isFileTypeAllowed(file, allowedTypes = ALLOWED_TYPES.ALL) {
  const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
  const validExtensions = {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/gif': ['.gif'],
    'image/webp': ['.webp'],
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
  };

  // Check MIME type
  if (allowedTypes.includes(file.type)) return true;

  // Check extension as fallback
  for (const [mimeType, extensions] of Object.entries(validExtensions)) {
    if (allowedTypes.includes(mimeType) && extensions.includes(fileExtension)) {
      return true;
    }
  }

  return false;
}

/**
 * Validate file size
 */
export function validateFileSize(file, maxSizeKB) {
  const fileSizeKB = file.size / 1024;
  if (fileSizeKB > maxSizeKB) {
    throw new Error(`File is too large. Maximum size is ${maxSizeKB}KB. Your file is ${fileSizeKB.toFixed(2)}KB.`);
  }
}

/**
 * Convert file to base64
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Process file - validate, compress if image, convert to base64
 * Returns { data: base64String, metadata: { fileName, fileType, fileSize, ... } }
 */
export async function processFile(file, options = {}) {
  const {
    maxSizeKB = null,
    allowedTypes = ALLOWED_TYPES.ALL,
    compress = true
  } = options;

  // Validate file type
  if (!isFileTypeAllowed(file, allowedTypes)) {
    const allowedExtensions = allowedTypes.map(type => {
      const ext = type.split('/')[1];
      return `.${ext}`;
    }).join(', ');
    throw new Error(`File type not allowed. Accepted types: ${allowedExtensions}`);
  }

  const fileName = file.name;
  const fileType = file.type;
  const originalSize = file.size;
  const uploadedAt = new Date().toISOString();

  // For images, compress if needed.
  // NOTE (FT-345): `FILE_SIZE_LIMITS.IMAGE` was removed to eliminate the
  // 50 KB / 2 MB silent-rejection mismatch. Callers that still pass images
  // through `processFile` (e.g. paper-checklist upload) must supply their
  // own `maxSizeKB` — otherwise we fall back to `compressImage`'s built-in
  // default (30 KB) via `undefined`. New photo capture must use
  // `<PhotoAttachment>` instead of this path.
  if (file.type.startsWith('image/') && compress) {
    const targetSize = maxSizeKB || undefined;
    try {
      const compressedImage = await compressImage(file, targetSize);
      const compressedSize = Math.round((compressedImage.length * 3) / 4 / 1024); // Estimate base64 size

      return {
        data: compressedImage,
        metadata: {
          fileName,
          fileType,
          originalSize,
          compressedSize,
          uploadedAt,
          compressed: true
        }
      };
    } catch (err) {
      throw new Error(`Image compression failed: ${err.message}`);
    }
  }

  // For other files (PDFs, documents), validate size and convert to base64
  const fileSizeKB = file.size / 1024;
  const maxSize = maxSizeKB || FILE_SIZE_LIMITS[getFileCategory(fileType).toUpperCase()] || FILE_SIZE_LIMITS.DEFAULT;

  validateFileSize(file, maxSize);

  const base64Data = await fileToBase64(file);

  return {
    data: base64Data,
    metadata: {
      fileName,
      fileType,
      fileSize: originalSize,
      uploadedAt,
      compressed: false
    }
  };
}

/**
 * Download file from base64 data
 */
export function downloadFile(base64Data, fileName) {
  const link = document.createElement('a');
  link.href = base64Data;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}
